import React from 'react';
import EmployeeAvatar from './EmployeeAvatar';
import '../../styles/employee-management.css';
import { TableSkeleton } from '../../../components/loading/skeletons';
import './EmployeeListView.css';

const IconView = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const IconDelete = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M6 19c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const IconEmail = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const sortIndicator = (active, direction) => {
  if (!active) return '↕';
  return direction === 'asc' ? '↑' : '↓';
};

const EmployeeListView = ({
  employees,
  isLoading = false,
  onViewProfile,
  onEdit,
  onDelete,
  getRoleColor,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  formatDate = () => '',
  canEdit,
  canDelete,
  companyData = null,
}) => {
  const handleSort = (property) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  if (isLoading) {
    return (
      <div className="app-table-wrap employee-list-view">
        <TableSkeleton
          rows={8}
          columns={[200, 120, 180, 120, 90]}
          label="Loading employees"
        />
      </div>
    );
  }

  return (
    <div className="app-table-wrap employee-list-view">
      <div className="employee-list-view__scroll">
        <table className="app-data-table employee-list-view__table" aria-label="Employees table">
          <thead>
            <tr>
              <th>
                <button type="button" className="app-table-sort" onClick={() => handleSort('name')}>
                  Employee <span aria-hidden="true">{sortIndicator(sortBy === 'name', sortDirection)}</span>
                </button>
              </th>
              <th>
                <button type="button" className="app-table-sort" onClick={() => handleSort('role')}>
                  Role <span aria-hidden="true">{sortIndicator(sortBy === 'role', sortDirection)}</span>
                </button>
              </th>
              <th>Contact</th>
              <th>
                <button type="button" className="app-table-sort" onClick={() => handleSort('date')}>
                  Joined <span aria-hidden="true">{sortIndicator(sortBy === 'date', sortDirection)}</span>
                </button>
              </th>
              <th className="app-table-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr className="app-data-table-empty-row">
                <td colSpan={5}>No employees found.</td>
              </tr>
            ) : employees.map((employee) => {
              const roleHue = getRoleColor(employee.role);
              const roleLabel = employee.role || 'Employee';

              return (
                <tr
                  key={employee.id}
                  className="employee-list-view__row"
                  style={{ '--employee-role-color': roleHue }}
                >
                  <td>
                    <div className="em-flex employee-list-view__employeeCell">
                      <EmployeeAvatar employee={employee} companyData={companyData} size="sm" />
                      <div>
                        <div className="employee-list-view__name">
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div className="em-text-muted">{employee.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className="employee-list-view__roleChip"
                      style={{
                        background: `${roleHue}1F`,
                        color: roleHue,
                      }}
                    >
                      {roleLabel}
                    </span>
                  </td>
                  <td>
                    {employee.email ? (
                      <a
                        className="app-icon-btn"
                        href={`mailto:${employee.email}`}
                        title="Send email"
                        aria-label={`Email ${employee.firstName} ${employee.lastName}`}
                      >
                        <IconEmail />
                      </a>
                    ) : (
                      <span className="em-text-muted">—</span>
                    )}
                  </td>
                  <td>{formatDate(employee.createdAt)}</td>
                  <td className="app-table-actions">
                    <div className="employee-list-view__actions">
                      <button
                        type="button"
                        className="app-icon-btn"
                        onClick={() => onViewProfile(employee.id)}
                        title="View profile"
                        aria-label={`View ${employee.firstName} ${employee.lastName}`}
                      >
                        <IconView />
                      </button>
                      {canEdit ? (
                        <button
                          type="button"
                          className="app-icon-btn"
                          onClick={() => onEdit(employee)}
                          title="Edit employee"
                          aria-label={`Edit ${employee.firstName} ${employee.lastName}`}
                        >
                          <IconEdit />
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          className="app-icon-btn app-icon-btn--danger"
                          onClick={() => onDelete(employee)}
                          title="Remove employee"
                          aria-label={`Remove ${employee.firstName} ${employee.lastName}`}
                        >
                          <IconDelete />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeListView;
